"use client";

import { ChevronDown, Mail, Phone, Clock, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RangeInput } from "@/components/ui/range-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card shadow-md">
        <p className="font-default text-2xl font-bold text-foreground mb-4">{successMessage}</p>
        <p className="text-sm text-muted-foreground mb-6">
          Our precision team will contact you shortly to schedule your site assessment.
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-steel-edger text-steel-edger hover:border-primary hover:text-primary">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Enter your name" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-0 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Email Address
            </FormLabel>
            <FormControl>
              <Input {...field} type="email" placeholder="acosta@example.com" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-0 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input {...field} type="tel" placeholder="682-320-5738" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-0 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Service Required
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-0 bg-transparent px-0 h-10 text-base text-foreground focus-visible:ring-0 rounded-md [&>svg]:text-primary">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border border-border">
                <SelectItem value="mowing">Precision Mowing</SelectItem>
                <SelectItem value="edging">Sharp Edging & Trimming</SelectItem>
                <SelectItem value="full_maintenance">Full Turf Maintenance</SelectItem>
                <SelectItem value="seasonal">Seasonal Cleanup</SelectItem>
                <SelectItem value="other">Other Request</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_location" render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Property Address
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Street, City, Zip Code" className="border-0 bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-0 rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" rules={{
      min: {
        value: 0,
        message: "Budget must be positive"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3 border-b-2 border-border pb-4 focus-within:border-primary transition-colors duration-300">
            <div className="flex items-baseline justify-between">
              <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
                Monthly Budget
              </FormLabel>
              <span className="font-mono text-sm text-primary font-bold">
                ${Number(field.value || 150).toLocaleString()}
              </span>
            </div>
            <FormControl>
              <RangeInput min={50} max={2000} step={25} value={Number(field.value || 150)} onValueChange={v => field.onChange(v)} className="w-full h-1 bg-border appearance-none cursor-pointer accent-primary rounded-md" />
            </FormControl>
            <div className="flex justify-between text-[10px] font-mono text-muted-foreground/60">
              <span>$50</span>
              <span>$500</span>
              <span>$1K</span>
              <span>$1.5K</span>
              <span>$2K+</span>
            </div>
          </FormItem>} />

      <FormField control={control} name="start_date" render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Requested Start Date
            </FormLabel>
            <FormControl>
              <Input {...field} type="date" className="border-0 bg-transparent px-0 h-10 text-base text-foreground focus-visible:ring-0 focus-visible:border-0 rounded-md [color-scheme:light]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "Please tell us about your lawn",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2 border-b-2 border-border pb-2 focus-within:border-primary transition-colors duration-300">
            <FormLabel className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Lawn Specifications
            </FormLabel>
            <FormControl>
              <Textarea {...field} rows={4} placeholder="Describe your lawn size, current condition, and specific edging needs..." className="border-0 bg-transparent px-0 min-h-28 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 resize-none rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-mono text-xs uppercase tracking-[0.2em] shadow-md transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : "Request Quote"}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-4 font-mono uppercase tracking-[0.15em]">
          Precision response within 24 hours
        </p>
      </div>
    </Form>;
}
export default function StartYourTransformation() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const clientTypes = ["RESIDENTIAL", "COMMERCIAL", "ESTATES"];
  return <section data-section-id="2455" id="start-your-transformation" className="bg-background text-foreground pt-32 md:pt-44 lg:pt-[200px] pb-20 md:pb-28 lg:pb-32 px-6 md:px-10 lg:px-16 relative overflow-hidden">
      <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-md pointer-events-none opacity-10" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)",
      filter: "blur(80px)"
    }} />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
          <div ref={leftRef} className={`flex flex-col transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                [CONTACT.01]
              </span>
            </div>

            <h2 className="font-default font-extrabold text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.9] tracking-tighter text-foreground mb-8">
              Start Your<br />
              Transformation.
            </h2>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md mb-12 font-default">
              From precision mowing to architectural edging, Acosta&apos;s Landscaping engineers the perfect outdoor environment for your property.
            </p>

            <div className="space-y-5 mb-16">
              <div className="flex items-center gap-4">
                <Mail className="size-4 text-primary stroke-[2]" />
                <Link to="mailto:acostalawncare@gmail.com" className="text-foreground text-sm hover:text-primary transition-colors font-default">
                  acostalawncare@gmail.com
                </Link>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="size-4 text-primary stroke-[2]" />
                <Link to="tel:6823205738" className="text-foreground text-sm hover:text-primary transition-colors font-default">
                  682-320-5738
                </Link>
              </div>
              <div className="flex items-center gap-4">
                <Clock className="size-4 text-primary stroke-[2]" />
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Mon–Sat 7AM–7PM
                </span>
              </div>
            </div>

            <div className="mt-auto">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 mb-4">
                Precision Services For
              </p>
              <div className="flex flex-wrap gap-3">
                {clientTypes.map((type, i) => <span key={type} data-index={i} className="px-4 py-2 border border-border rounded-md font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-300">
                    {type}
                  </span>)}
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-primary/60 pointer-events-none" />
              <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-primary/60 pointer-events-none" />
              <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-primary/60 pointer-events-none" />
              <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-primary/60 pointer-events-none" />

              <div className="bg-card border border-border p-8 md:p-12 rounded-md shadow-lg">
                <div className="flex items-baseline justify-between mb-10 pb-6 border-b border-border">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    Service Request
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                    SPEC / 001
                  </span>
                </div>

                <FormProvider formKey="transformation_form_get_quote" formId="transformation_form_get_quote" sectionName="start-a-project" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_type: "",
                project_location: "",
                budget: 150,
                start_date: "",
                project_description: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                project_description: {
                  required: true,
                  minLength: 10
                },
                budget: {
                  min: 0
                }
              }} submitText="Request Quote" successMessage="Thank you. Your request has been logged." errorMessage="Submission failed. Please call us directly at 682-320-5738.">
                  <ProjectForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}